import { Fragment } from 'react';
import { View } from 'react-native';

import { Avatar, Center, HStack, Text, VStack, theme } from '@squire/ui';

import { InfoCircleFilledIcon, UserIcon } from '@/components/_icons';
import { Card } from '@/components/_shared/Card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/_shared/Tooltip';
import { PriceRangeTooltip } from '@/components/booking/PriceRangeTooltip';
import { TipSelector } from '@/components/booking/TipSelector';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import type { CartItem } from '@/types/booking';
import {
  getCartItemRangePrice,
  getCartItemTotal,
  hasSubItemPriceRange,
  isAnyProfessionalCartItem,
} from '@/utils/bookingCartUtils';
import {
  DateFormat,
  formatDate,
  formatDuration,
  parseDate,
  parseDateWithTimezone,
} from '@/utils/dateUtils';
import { formatPrice } from '@/utils/internationalizationUtils';
import { formatWaitlistTimePreferences } from '@/utils/waitlistTimePreferences';

import { styles } from './styles';
import type { ViewOrderCartItemProps } from './types';

const ViewOrderCartItem = ({
  cartItem,
  shopTimezone,
  canAddTip,
  showTipLine,
  isSameBarberGroup,
  onSelectTip,
  selectedTipAmount,
  loading = false,
  countTipInPrice = false,
  currency = 'USD',
  waitlistDate,
  isStandbyWaitlist = false,
  waitlistTimePreferences,
  depositCard,
  showPriceRangeTooltip = false,
}: ViewOrderCartItemProps) => {
  const { t, i18n } = useScreenTranslations('ViewOrderBottomSheet');
  const { t: tPicker } = useScreenTranslations('WaitlistTimePreferencePicker');

  const cartSubItems = cartItem.subItems || [];

  const selectedTime =
    cartItem.dateTime && shopTimezone
      ? formatDate(
          parseDateWithTimezone(cartItem.dateTime, shopTimezone ?? ''),
          DateFormat.CART_ITEM_TIME,
          i18n.language,
        )
      : '';

  const waitlistLabel = waitlistDate
    ? t(isStandbyWaitlist ? 'joinWaitlistFor' : 'waitlistMeFor', {
        date: formatDate(
          parseDate(waitlistDate),
          DateFormat.CART_ITEM_DATE,
          i18n.language,
        ),
      })
    : null;

  const preferredTimeValue = formatWaitlistTimePreferences(
    waitlistTimePreferences ?? [],
    (key) => tPicker(key),
  );

  const totalDuration = cartSubItems?.reduce(
    (acc, subItem) => acc + subItem.duration,
    0,
  );

  const totalCost = getCartItemTotal(
    cartItem,
    countTipInPrice,
    selectedTipAmount,
  );
  const itemRangePrice = getCartItemRangePrice(cartItem);

  const getBarberName = () => {
    if (cartItem.barber) {
      const { firstName, lastName } = cartItem.barber;
      return `${firstName} ${lastName ? `${lastName.at(0)}.` : ''}`;
    }
    return t('guestAppointment');
  };

  const isAnyProfessional = isAnyProfessionalCartItem(cartItem);

  const renderSubItem = (
    cartSubItem: CartItem['subItems'][number],
    index: number,
    showPlus = false,
  ) => {
    const subItemHasRange = hasSubItemPriceRange(cartSubItem);
    const showTooltip =
      showPriceRangeTooltip && isAnyProfessional && subItemHasRange;

    const subItemPriceText = subItemHasRange
      ? `${formatPrice(cartSubItem.rangeDisplayPrice.min / 100, { locale: i18n.language, currency })} - ${formatPrice(cartSubItem.rangeDisplayPrice.max / 100, { locale: i18n.language, currency })}`
      : formatPrice((cartSubItem.cost ?? 0) / 100, {
          locale: i18n.language,
          currency,
        });

    return (
      <View style={styles.spaceBetween}>
        <>
          <View style={styles.subItemName}>
            <Text
              variant="m"
              numberOfLines={1}
              color="cartTextSecondary"
              style={styles.subItemNameText}
            >
              {`${showPlus ? '+ ' : ''} ${cartSubItem.name}`}
            </Text>

            {showTooltip && (
              <PriceRangeTooltip
                variant="cart"
                testID={`view-order-price-range-tooltip-${cartItem.id}-${index}`}
              />
            )}
          </View>

          <Text variant="m" color="cartTextSecondary" align="right">
            {subItemPriceText}
          </Text>
        </>
      </View>
    );
  };

  return (
    <Card style={styles.barberCard} padding={theme.spacing[5]}>
      <HStack space={theme.spacing[5]}>
        {cartItem.bookedWithAnyBarber && !cartItem.barber ? (
          <Center style={styles.barberAvatar}>
            <UserIcon color="cartIconPrimary" size={24} />
          </Center>
        ) : (
          <Avatar
            shape="square"
            uri={cartItem.barber?.photos?.[0]?.url}
            fallbackText={getBarberName()}
            style={styles.barberAvatar}
          />
        )}

        <VStack flex space={theme.spacing[1]}>
          <HStack style={styles.spaceBetween}>
            <>
              <Text variant="lStrong" color="cartTextPrimary" numberOfLines={1}>
                {cartItem.bookedWithAnyBarber && !cartItem.barber
                  ? t('anyProfessional')
                  : getBarberName()}
              </Text>

              {!!cartSubItems?.length && (
                <Text variant="lStrong" color="cartTextPrimary">
                  {itemRangePrice
                    ? `${formatPrice(itemRangePrice.min / 100, { locale: i18n.language, currency })} - ${formatPrice(itemRangePrice.max / 100, { locale: i18n.language, currency })}`
                    : formatPrice((totalCost ?? 0) / 100, {
                        locale: i18n.language,
                        currency,
                      })}
                </Text>
              )}
            </>
          </HStack>

          {!!cartSubItems.length && renderSubItem(cartSubItems[0], 0)}
        </VStack>
      </HStack>

      {/* Render sub items */}
      {cartSubItems.slice(1).map((cartSubItem, index) => (
        <Fragment key={cartSubItem.id || `sub-item-${cartSubItem.name}`}>
          {renderSubItem(cartSubItem, index + 1, true)}
        </Fragment>
      ))}

      {(selectedTime || waitlistLabel) && (
        <HStack style={styles.spaceBetween}>
          <>
            <Text variant="m" color="cartTextSecondary">
              {selectedTime || waitlistLabel}
            </Text>

            <Text variant="m" color="cartTextSecondary">
              {formatDuration(totalDuration)}
            </Text>
          </>
        </HStack>
      )}

      {isStandbyWaitlist && !!waitlistDate && (
        <View
          style={styles.spaceBetween}
          accessible
          accessibilityLabel={`${t('preferredTime')}: ${preferredTimeValue}`}
        >
          <Text variant="m" color="textSecondaryInverse">
            {t('preferredTime')}
          </Text>

          <Text variant="m" color="textSecondaryInverse" align="right">
            {preferredTimeValue}
          </Text>
        </View>
      )}

      {(canAddTip || showTipLine) && (
        <>
          <HStack style={styles.spaceBetween}>
            <>
              {canAddTip ? (
                <Tooltip placement="bottom">
                  <TooltipTrigger>
                    <HStack centerVertical space={theme.spacing[2]}>
                      <>
                        <Text variant="m" color="cartTextSecondary">
                          {t('tip')}
                        </Text>
                      </>
                      <InfoCircleFilledIcon
                        size={16}
                        color="cartIconTertiary"
                      />
                    </HStack>
                  </TooltipTrigger>
                  <TooltipContent>
                    <>
                      <Text variant="m" color="textPrimaryInverse">
                        {isSameBarberGroup
                          ? t('tipTooltipGroup')
                          : t('tipTooltip')}
                      </Text>
                    </>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Text variant="m" color="cartTextSecondary">
                  {t('tip')}
                </Text>
              )}

              {selectedTipAmount !== undefined && (
                <Text variant="m" color="cartTextSecondary">
                  {formatPrice(selectedTipAmount / 100, {
                    locale: i18n.language,
                    currency,
                  })}
                </Text>
              )}
            </>
          </HStack>
          {canAddTip && (
            <TipSelector
              tips={cartItem.availableTips}
              selectedTipAmount={selectedTipAmount ?? 0}
              onSelectTip={onSelectTip}
              selectedTipPercentage={cartItem.tipPercentage}
              loading={loading}
              currency={currency}
            />
          )}
        </>
      )}

      {depositCard}
    </Card>
  );
};

ViewOrderCartItem.displayName = 'ViewOrderCartItem';
export { ViewOrderCartItem };
